import { LegacyContentStatus } from '@lumapps/contents/types';
import { ColorPalette } from '@lumapps/lumx/react';
import { GLOBAL } from '@lumapps/translations';

/** Individual fields requested from content/list for every item. */
export const CONTENT_TYPE_ADMIN_CONTENT_LIST_FIELDS = [
    'analyticsViewSum',
    'archivers',
    'author',
    'authorDetails{firstName,lastName,id}',
    'createdAt',
    'updatedByDetails{firstName,lastName,id}',
    'writerDetails{firstName,lastName,id}',
    'customContentType',
    'customContentTypeDetails',
    'customContentTypeTags',
    'deleters',
    'editors',
    'endDate',
    'featuredEndDate',
    'featuredFeedKeys',
    'featuredStartDate',
    'feedKeys',
    'id',
    'isFeatured',
    'lastRevision',
    'metadata',
    'publicationDate',
    'publishers',
    'renderingType',
    'slug',
    'startDate',
    'status',
    'mediaThumbnail',
    'thumbnail',
    'title',
    'uid',
    'updatedAt',
];

export const MAX_RESULTS = 30;

/**
 * Default status filter when workflow is disabled (applies to both admin and contributors).
 * ARCHIVE is excluded intentionally — users navigate to archived content explicitly via the filter.
 */
export const DEFAULT_STATUS_FILTER = [LegacyContentStatus.draft, LegacyContentStatus.live] as const;

/**
 * Default status filter when workflow is enabled.
 * TO_VALIDATE is included so contributors immediately see content pending their validation action.
 */
export const DEFAULT_STATUS_WORKFLOW_FILTER = [
    LegacyContentStatus.draft,
    LegacyContentStatus.live,
    LegacyContentStatus.toValidate,
] as const;

/** Status filter forced when the Publishable tab is active. */
export const DEFAULT_STATUS_PUBLISHABLE_TAB = [LegacyContentStatus.toValidate] as const;

/** Action values sent to the content/list API. */
export const CONTENT_ACTIONS = {
    EDIT: 'PAGE_EDIT',
    DELETE: 'PAGE_DELETE',
    ARCHIVE: 'PAGE_ARCHIVE',
    PUBLISH: 'PAGE_PUBLISH',
} as const;

/** Option IDs for the expiration checkbox filter — shared between the filter component and convertFiltersToApiParams. */
export const EXPIRATION_FILTER_OPTIONS = {
    EXPIRING_SOON: 'expiringSoon',
    EXPIRED: 'expired',
} as const;

/** Column/cell IDs used in Heads.Base and Cells.Base — also serve as API sort keys. */
export const COLUMN_KEYS = {
    TITLE: 'title',
    STATUS: 'status',
    AUTHOR: 'author',
    UPDATED_AT: 'updatedAt',
    VIEWS: 'views',
    END_DATE: 'endDate',
} as const;

export const DEFAULT_SORTING = `-${COLUMN_KEYS.UPDATED_AT}` as const;

export const SCOPE = 'content-type-admin-content';
export const HEADER_SCOPE = `${SCOPE}-header`;
export const TABLE_SCOPE = `${SCOPE}-list`;
export const FILTERS_SCOPE = `${SCOPE}-filters`;
export const DRAWER_SCOPE = `${SCOPE}-drawer`;

// Follows the STATUS_CONFIG pattern from journeys/broadcasts/constants.ts.
// Only four statuses appear as content.status on this page
// CONTENT_STATUS_COLOR map exactly. REFUSED, UNARCHIVE and PENDING never appear as
// content.status here; they only surface via lastRevision.status (handled by getRevisionHint).
// TO_VALIDATE uses the same label as DRAFT (V2 wording: Unpublished)
// where TO_VALIDATE renders the CONTENT.STATUS_DRAFT translation key.
export const STATUS_CONFIG = {
    labels: {
        [LegacyContentStatus.draft]: GLOBAL.UNPUBLISHED,
        [LegacyContentStatus.live]: GLOBAL.PUBLISHED,
        [LegacyContentStatus.archive]: GLOBAL.ARCHIVED,
        [LegacyContentStatus.toValidate]: GLOBAL.UNPUBLISHED,
    },
    colors: {
        [LegacyContentStatus.draft]: ColorPalette.dark,
        [LegacyContentStatus.live]: ColorPalette.green,
        [LegacyContentStatus.archive]: ColorPalette.yellow,
        [LegacyContentStatus.toValidate]: ColorPalette.dark,
    },
} as const;
